import * as React from "react";
import { InfoWindowF } from "@react-google-maps/api";

import type { Stop, Zone } from "../../../types";
import { getMunicipalitiesForLine } from "../../../api/zones";
import { isPureNartrafikFromStop } from "../../../utils/stopType";
import { rootIdOf } from "../../Map/transitMap/helpers";
import { Icon, Badge, Heading, Button } from "@ogt/ui";

type ZoneInfoWindowProps = {
  zone: Zone;
  position: google.maps.LatLngLiteral;
  stops: Stop[];
  onStopSelect: (stop: Stop) => void;
  onClose?: () => void;
};

const SV_COLLATOR = new Intl.Collator("sv", {
  sensitivity: "base",
  numeric: true,
});

export default function ZoneInfoWindow({
  zone,
  position,
  stops,
  onStopSelect,
  onClose,
}: ZoneInfoWindowProps) {
  const label = zone.name || "Närtrafikområde";
  const lineId = String(zone.id ?? "").split("_")[0];

  const [municipalities, setMunicipalities] = React.useState<string[]>([]);
  const [loadingMunis, setLoadingMunis] = React.useState(false);

  const relatedStops = React.useMemo(() => {
    const byRoot = new Map<string, Stop>();

    for (const stop of stops) {
      const matchesLine = (stop.servedLineIds ?? []).some(
        (id) => String(id).trim() === lineId
      );

      if (!isPureNartrafikFromStop(stop) || !matchesLine) continue;

      const rootId = rootIdOf(stop.id);
      const existing = byRoot.get(rootId);

      if (!existing || String(stop.id) === rootId) {
        byRoot.set(rootId, stop);
      }
    }

    return Array.from(byRoot.values()).sort((a, b) =>
      SV_COLLATOR.compare(a.name, b.name)
    );
  }, [stops, lineId]);

  React.useEffect(() => {
    let cancelled = false;

    setMunicipalities([]);

    const lineNrReal = Number(lineId);

    if (!lineId || !Number.isFinite(lineNrReal)) {
      setLoadingMunis(false);
      return;
    }

    setLoadingMunis(true);

    getMunicipalitiesForLine(lineNrReal)
      .then((items) => {
        if (!cancelled) setMunicipalities(items ?? []);
      })
      .catch(() => {
        if (!cancelled) setMunicipalities([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMunis(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lineId]);

  return (
    <InfoWindowF
      position={position}
      onCloseClick={onClose}
      options={{
        disableAutoPan: false,
        pixelOffset: new google.maps.Size(0, -12),
      }}
    >
      <div className="pt-0 pl-4 pr-6 pb-6 min-w-[180px] max-w-[320px] text-sm leading-snug bg-[var(--semantic-color-bg-primary)]">
        {municipalities.length > 0 && (
          <div className="flex gap-2 mb-4 items-center">
            <Badge
              icons={[<Icon key="n" name="n" />]}
              shape="default"
              state="default"
              variant="blue"
            />

            <Heading as="h4" className=" mb-0">
              {municipalities.join(", ")}
            </Heading>
          </div>
        )}

        <Heading as="h5">{label}</Heading>

        {loadingMunis && (
          <div className="mt-1" role="status">
            Hämtar kommun…
          </div>
        )}

        <div className="mt-4">
          <p className="mb-2 font-medium">Hållplatser för närtrafiken</p>

          {relatedStops.length > 0 ? (
            <ul className="m-0 p-0 list-none flex flex-col gap-1 max-h-[240px] overflow-y-auto">
              {relatedStops.map((stop) => (
                <li key={rootIdOf(stop.id)}>
                  <Button
                    variant="secondary"
                    aria-label={`Aktivera ${stop.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onStopSelect(stop);
                    }}
                  >
                    <Badge variant="blue">
                      <Icon size="10" name="n" />
                    </Badge>

                    <span className="min-w-0 flex-1">{stop.name}</span>
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-0">
              Inga kopplade närtrafikhållplatser hittades i närtrafiksområdet.
            </p>
          )}
        </div>
      </div>
    </InfoWindowF>
  );
}
